import Carousel from '@molecules/Carousel/Carousel';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperOptions } from 'swiper/types';

type PaginationSwiperOptions = Pick<
  SwiperOptions,
  | 'autoplay'
  | 'loop'
  | 'slidesPerView'
  | 'spaceBetween'
  | 'centeredSlides'
  | 'initialSlide'
  | 'breakpoints'
>;

interface Props extends PaginationSwiperOptions {
  readonly children: ReactNode[];
  readonly dataTestId?: string;
  readonly id?: string;
  readonly customPaginationId?: string;
  readonly paginationClasses?: string;
  readonly slideChangeCallback?: (swiper: Swiper) => void;
  readonly swiperClasses?: string;
}

const PaginationCarousel = ({
  autoplay,
  breakpoints,
  centeredSlides = false,
  children,
  dataTestId = 'Pagination-Carousel',
  id = '',
  customPaginationId = '',
  initialSlide = 0,
  loop = false,
  paginationClasses,
  slideChangeCallback,
  swiperClasses,
  slidesPerView = 1,
  spaceBetween = 20,
}: Props) => {
  if (!children) {
    return null;
  }

  const carouselId = customPaginationId || `${id}-pagination-carousel-swiper`;

  return (
    <div className="relative select-none" data-testid={dataTestId}>
      <Carousel
        additionalModules={[Pagination, ...(autoplay ? [Autoplay] : [])]}
        className={swiperClasses}
        loop={loop}
        autoplay={autoplay}
        onSlideChange={(swiper) => {
          slideChangeCallback?.(swiper);
        }}
        pagination={{
          el: `#${carouselId}`,
          clickable: true,
        }}
        slidesPerView={slidesPerView}
        spaceBetween={spaceBetween}
        navId={id}
        centeredSlides={centeredSlides}
        initialSlide={initialSlide}
        breakpoints={breakpoints}
      >
        {children}
      </Carousel>

      <div
        className={classNames('visible', paginationClasses, {
          hidden: children.length === 0,
        })}
      >
        <div id={carouselId} className="pointer-events-none flex gap-4" />
      </div>
    </div>
  );
};

export default PaginationCarousel;
